import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Pencil, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, authHeaders, formatApiError } from "@/lib/api";

const STATUSES = ["New", "Contacted", "In Progress", "Admitted", "Closed"];

const ENTITIES = {
  programs: {
    label: "Programs",
    fields: [
      { k: "name", label: "Program Name" },
      { k: "university", label: "University" },
      { k: "degree", label: "Degree", type: "select", options: ["Bachelor's", "Master's"] },
      { k: "field", label: "Field" },
      { k: "language", label: "Language", placeholder: "English" },
      { k: "duration", label: "Duration", placeholder: "e.g. 4 years" },
      { k: "tuition_range", label: "Tuition Range (indicative)" },
      { k: "location", label: "City" },
      { k: "destination", label: "Destination", placeholder: "Romania" },
    ],
    cols: (it) => [it.name, it.university, it.degree, it.field],
    headers: ["Program", "University", "Degree", "Field"],
  },
  universities: {
    label: "Universities",
    fields: [
      { k: "name", label: "University Name" },
      { k: "city", label: "City" },
      { k: "fields", label: "Fields / Notes" },
      { k: "badge", label: "Badge (optional)" },
    ],
    cols: (it) => [it.name, it.city, it.fields],
    headers: ["Name", "City", "Fields"],
  },
  faqs: {
    label: "FAQs",
    fields: [
      { k: "question", label: "Question" },
      { k: "answer", label: "Answer", type: "textarea" },
      { k: "category", label: "Category", type: "select", options: ["General Study Abroad", "Country Specific", "Admissions", "English Tests", "Scholarships", "Tuition", "Visa", "Accommodation", "Applications", "Intakes"] },
    ],
    cols: (it) => [it.question, it.category],
    headers: ["Question", "Category"],
  },
  testimonials: {
    label: "Testimonials",
    fields: [
      { k: "name", label: "Student Name" },
      { k: "program", label: "Program" },
      { k: "university", label: "University" },
      { k: "country", label: "Country" },
      { k: "quote", label: "Quote", type: "textarea" },
      { k: "photo_url", label: "Photo URL (optional)" },
    ],
    cols: (it) => [it.name, it.program, it.university],
    headers: ["Name", "Program", "University"],
  },
  posts: {
    label: "Blog Posts",
    fields: [
      { k: "title", label: "Article Title" },
      { k: "category", label: "Category", type: "select", options: ["Study Abroad Guides", "Country Updates", "University Insights", "Scholarships", "Visa & Documentation", "Student Life", "Education Trends", "Career-Focused Education", "Application Tips", "Romania", "Europe"] },
      { k: "slug", label: "URL Slug (optional — auto from title)" },
      { k: "cover_image", label: "Cover Image URL (optional)" },
      { k: "excerpt", label: "Short Excerpt", type: "textarea" },
      { k: "content", label: "Article Content (blank line = new paragraph, '## ' = heading)", type: "textarea" },
    ],
    cols: (it) => [it.title, it.category],
    headers: ["Title", "Category"],
  },
  countries: {
    label: "Countries",
    fields: [
      { k: "name", label: "Country Name" },
      { k: "flag", label: "Flag Emoji (e.g. 🇬🇧)" },
      { k: "tagline", label: "Tagline" },
      { k: "slug", label: "URL Slug (optional — auto from name)" },
      { k: "image", label: "Signature Image URL" },
      { k: "popular_courses", label: "Popular Courses (comma separated)" },
      { k: "content", label: "Country Guide Content ('## ' = section heading)", type: "textarea" },
    ],
    cols: (it) => [it.name, it.tagline],
    headers: ["Country", "Tagline"],
  },
  courses: {
    label: "Courses",
    fields: [
      { k: "name", label: "Course Name" },
      { k: "category", label: "Category", type: "select", options: ["Business & Management", "Computer Science & IT", "Engineering", "Healthcare & Life Sciences", "Finance & Economics", "Hospitality & Tourism", "Social Sciences"] },
      { k: "level", label: "Level", type: "select", options: ["Bachelor's", "Master's", "Both"] },
      { k: "duration", label: "Typical Duration" },
      { k: "mode", label: "Study Mode", placeholder: "Full-time" },
      { k: "slug", label: "URL Slug (optional — auto from name)" },
      { k: "countries", label: "Countries (comma separated)" },
      { k: "overview", label: "Course Overview", type: "textarea" },
      { k: "who_for", label: "Who Is This Course Suitable For?", type: "textarea" },
      { k: "background", label: "Typical Academic Background", type: "textarea" },
      { k: "subjects", label: "Key Subjects", type: "textarea" },
      { k: "career", label: "Career Relevance", type: "textarea" },
    ],
    cols: (it) => [it.name, it.category, it.level],
    headers: ["Course", "Category", "Level"],
  },
  scholarships: {
    label: "Scholarships",
    fields: [
      { k: "name", label: "Scholarship Name" },
      { k: "country", label: "Country" },
      { k: "institution", label: "Institution / Provider" },
      { k: "eligibility", label: "Eligibility", type: "textarea" },
      { k: "deadline", label: "Deadline" },
      { k: "funding", label: "Funding Details", type: "textarea" },
      { k: "source_url", label: "Official Source URL" },
      { k: "last_verified", label: "Last Verified Date" },
    ],
    cols: (it) => [it.name, it.country, it.deadline],
    headers: ["Scholarship", "Country", "Deadline"],
  },
  events: {
    label: "Events",
    fields: [
      { k: "title", label: "Event Title" },
      { k: "date", label: "Date" },
      { k: "time", label: "Time" },
      { k: "location", label: "Location / Online" },
      { k: "topic", label: "Topic" },
      { k: "speaker", label: "Speaker / University (optional)" },
      { k: "description", label: "Description", type: "textarea" },
    ],
    cols: (it) => [it.title, it.date, it.location],
    headers: ["Event", "Date", "Location"],
  },
  updates: {
    label: "Education Updates",
    fields: [
      { k: "title", label: "Update Title" },
      { k: "category", label: "Category", type: "select", options: ["Policy Updates", "University Announcements", "Intake Updates", "Scholarship Deadlines", "International Education"] },
      { k: "summary", label: "Summary", type: "textarea" },
      { k: "source", label: "Source (official university/government source)" },
    ],
    cols: (it) => [it.title, it.category],
    headers: ["Update", "Category"],
  },
};

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/30 focus:border-[#1E40AF]";

function Login({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      localStorage.setItem("hg_admin_token", data.token);
      onLogin(data.token);
      toast.success("Welcome back, Harwell Admin.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070E1E] flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <img src="/harwell-logo-transparent.png" alt="Harwell Global Education" className="h-14 w-auto mx-auto" />
        </div>
        <h1 className="text-lg font-extrabold text-[#0B192C] text-center mb-6">Admin Console</h1>
        <div className="space-y-3.5">
          <input data-testid="admin-login-email" type="email" required placeholder="Email" className={inputCls}
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="admin-login-password" type="password" required placeholder="Password" className={inputCls}
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <button data-testid="admin-login-submit-btn" disabled={loading}
            className="w-full py-3 rounded-xl text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] transition-all disabled:opacity-60">
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </button>
        </div>
      </form>
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leads${status ? `?status=${status}` : ""}`, { headers: authHeaders() });
      setLeads(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const setLeadStatus = async (id, s) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status: s }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: s } : l)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Type", "Phone", "Email", "Qualification", "Level", "Destination", "Course", "Status", "Created"],
      ...leads.map((l) => [l.name, l.type, l.phone, l.email || "", l.qualification || "", l.study_level || "", l.destination || "", l.course || "", l.status, l.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "harwell-leads.csv";
    a.click();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <select data-testid="admin-status-filter-select" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls + " w-44"}>
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button onClick={load} className="p-2 rounded-lg border border-slate-300 hover:bg-slate-100" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <button onClick={exportCsv} data-testid="admin-export-csv-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-[#0B192C] hover:bg-[#12233E]">
          <Download className="w-3.5 h-3.5" /> EXPORT CSV
        </button>
      </div>
      <div className="overflow-x-auto bg-white rounded-xl border border-slate-200" data-testid="admin-leads-data-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-data uppercase tracking-wider text-slate-500">
              {["Name", "Type", "Phone", "Email", "Level", "Destination", "Course", "Date", "Status"].map((h) => (
                <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-lead-row-${l.id}`}>
                <td className="px-4 py-3 font-semibold text-[#0B192C] whitespace-nowrap">{l.name}</td>
                <td className="px-4 py-3"><span className="text-[11px] font-data uppercase bg-[#E0F2FE] text-[#1E40AF] px-2 py-0.5 rounded-full">{l.type}</span></td>
                <td className="px-4 py-3 whitespace-nowrap">{l.phone}</td>
                <td className="px-4 py-3">{l.email || "—"}</td>
                <td className="px-4 py-3">{l.study_level || "—"}</td>
                <td className="px-4 py-3">{l.destination || "—"}</td>
                <td className="px-4 py-3 max-w-[200px] truncate">{l.course || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                <td className="px-4 py-3">
                  <select value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)}
                    data-testid={`admin-lead-status-${l.id}`}
                    className="text-xs rounded-lg border border-slate-300 px-2 py-1.5 bg-white">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && leads.length === 0 && <p className="text-center text-slate-400 py-10 text-sm">No leads yet.</p>}
        {loading && <p className="text-center text-slate-400 py-10 text-sm">Loading...</p>}
      </div>
    </div>
  );
}

function CrudTab({ entity }) {
  const cfg = ENTITIES[entity];
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/content/${entity}`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, [entity]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/content/${entity}/${editing}`, form, { headers: authHeaders() });
        toast.success("Updated.");
      } else {
        await api.post(`/admin/content/${entity}`, form, { headers: authHeaders() });
        toast.success("Added.");
      }
      setForm({}); setEditing(null); setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/content/${entity}/${id}`, { headers: authHeaders() });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEdit = (item) => {
    const f = {};
    cfg.fields.forEach(({ k }) => { f[k] = item[k] || ""; });
    setForm(f); setEditing(item.id); setShowForm(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-slate-500">{items.length} items</p>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({}); }}
          data-testid={`admin-add-${entity}-btn`}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8]">
          <Plus className="w-3.5 h-3.5" /> ADD {cfg.label.toUpperCase().slice(0, -1)}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid={`admin-${entity}-form`} className="bg-white rounded-xl border border-slate-200 p-6 mb-6 grid sm:grid-cols-2 gap-4">
          {cfg.fields.map((f) => (
            <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea rows={3} className={inputCls} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`admin-${entity}-input-${f.k}`} />
              ) : f.type === "select" ? (
                <select className={inputCls} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`admin-${entity}-input-${f.k}`}>
                  <option value="">Select...</option>
                  {f.options.map((o) => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input className={inputCls} placeholder={f.placeholder || ""} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`admin-${entity}-input-${f.k}`} />
              )}
            </div>
          ))}
          <div className="sm:col-span-2 flex gap-3">
            <button type="submit" data-testid={`admin-${entity}-save-btn`} className="px-5 py-2.5 rounded-lg text-xs font-bold text-white bg-[#059669] hover:bg-[#047857]">
              {editing ? "SAVE CHANGES" : "ADD"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-5 py-2.5 rounded-lg text-xs font-bold text-slate-600 border border-slate-300">CANCEL</button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto bg-white rounded-xl border border-slate-200" data-testid={`admin-${entity}-table`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-data uppercase tracking-wider text-slate-500">
              {cfg.headers.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                {cfg.cols(it).map((c, i) => <td key={i} className="px-4 py-3 max-w-[280px] truncate">{c}</td>)}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(it)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" aria-label="Edit" data-testid={`admin-${entity}-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(it.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" aria-label="Delete" data-testid={`admin-${entity}-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-center text-slate-400 py-10 text-sm">No items yet.</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("hg_admin_token"));
  const [tab, setTab] = useState("leads");
  const navigate = useNavigate();

  if (!token) return <Login onLogin={setToken} />;

  const tabs = [["leads", "Leads"], ...Object.entries(ENTITIES).map(([k, v]) => [k, v.label])];

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="bg-[#070E1E] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/harwell-logo.png" alt="Harwell" className="h-11 w-auto rounded-lg" />
            <span className="text-xs font-data uppercase tracking-[0.2em] text-slate-400 hidden sm:block">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} data-testid="admin-view-site-btn" className="text-xs font-semibold text-slate-300 hover:text-white">View Site</button>
            <button
              onClick={() => { localStorage.removeItem("hg_admin_token"); setToken(null); }}
              data-testid="admin-logout-btn"
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-7">
          {tabs.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${tab === k ? "bg-[#0B192C] text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "leads" ? <LeadsTab /> : <CrudTab entity={tab} />}
      </div>
    </div>
  );
}
